import { apiErrors } from '../constants/common';
import * as EVENTS from '../constants/external-events';
import { VISA } from '../constants/partnering-networks';
import { OTP_EVENT_NAME, VSRCI_OTP_EVENTS } from '../constants/srci-gtm-events';
import getNetworkInstance from '../helpers/get-network-instance';
import getOtpDeliveryChannel from '../helpers/get-otp-delivery-channel';
import {
  enrichDataLayer,
  getGtmData,
  trackInitiateIdentityValidation,
  trackINOEvent
} from '../helpers/gtm';
import OrchCustomData from '../helpers/orch-custom-data';
import promiseTimeout from '../helpers/promise-timeout';
import sendMessageToVisaSrci from '../helpers/send-message-to-visa-srci';

const promiseWrapper = function(instance, data, method) {
  return new Promise(resolve => {
    if (instance === null || instance === undefined) {
      const resultData = {
        error: apiErrors.NO_INSTANCE
      };
      resolve(resultData);
    }
    const networkPreferred = OrchCustomData.srcSdks?.orchConfig?.preferredNetwork ?? VISA;
    const vco = getNetworkInstance(OrchCustomData, VISA);
    instance[method](data)
      .then(data => {
        const maskedValidationChannel = data.maskedValidationChannel;
        const otpDeliveryChannel = getOtpDeliveryChannel(maskedValidationChannel);
        OrchCustomData.setOTPDeliveryChannel(otpDeliveryChannel);
        OrchCustomData.apiTimeStamp.initiateIdentityValidation[
          networkPreferred
        ].endTime = Date.now();
        OrchCustomData.apiTimeStamp.initiateIdentityValidation[
          networkPreferred
        ].timeTakenInMillSeconds =
          OrchCustomData.apiTimeStamp.initiateIdentityValidation[networkPreferred].endTime -
          OrchCustomData.apiTimeStamp.initiateIdentityValidation[networkPreferred].startTime;
        const step = `ORCH_INITIATE_IDENTITY_VALIDATION_${networkPreferred}_API_SUCCESS_RESPONSE`;
        if (vco && typeof vco.pushEvents === 'function') {
          vco.pushEvents({
            E: step,
            ERRCODE: null,
            ERRMSG: null,
            ETIME: OrchCustomData.apiTimeStamp.initiateIdentityValidation[networkPreferred].endTime,
            LL: 'INFO',
            S: 200,
            STIME:
              OrchCustomData.apiTimeStamp.initiateIdentityValidation[networkPreferred].startTime,
            TY: 'RESPONSE',
            checkout_step: step,
            response: data,
            srcInitiatorId: OrchCustomData.srcInitiatorId[networkPreferred]
          });
        }
        resolve({
          customData: {
            otpDeliveryChannel
          },
          maskedValidationChannel
        });
      })
      .catch(err => {
        const { error } = err;
        OrchCustomData.apiTimeStamp.initiateIdentityValidation[
          networkPreferred
        ].endTime = Date.now();
        OrchCustomData.apiTimeStamp.initiateIdentityValidation[
          networkPreferred
        ].timeTakenInMillSeconds =
          OrchCustomData.apiTimeStamp.initiateIdentityValidation[networkPreferred].endTime -
          OrchCustomData.apiTimeStamp.initiateIdentityValidation[networkPreferred].startTime;
        const stepErr = `ORCH_INITIATE_IDENTITY_VALIDATION_${networkPreferred}_API_ERROR_RESPONSE`;
        if (vco && typeof vco.pushEvents === 'function') {
          vco.pushEvents({
            E: stepErr,
            ERRCODE: err && err.error_code ? err.error_code : '',
            ERRMSG: err && err.msg ? err.msg : '',
            ETIME: OrchCustomData.apiTimeStamp.initiateIdentityValidation[networkPreferred].endTime,
            LL: 'INFO',
            S: null,
            STIME:
              OrchCustomData.apiTimeStamp.initiateIdentityValidation[networkPreferred].startTime,
            TY: 'RESPONSE',
            checkout_step: stepErr,
            response: error,
            srcInitiatorId: OrchCustomData.srcInitiatorId[networkPreferred]
          });
        }
        resolve({ error });
      });
  });
};

/*
* SRCi needs to pass the GTM data layer to ORCH layer. This will help to retain the custom attributes in GTM
* Added as part of GTM implementation on initiateIdentityValidation and completeIdentityValidation APIs ref bug #VCO-13577
*/
const initiateIdentityValidation = inputData => {
  return new Promise(resolve => {
    let vsbGtmData = {};
    OrchCustomData.apiTimeStamp.initiateIdentityValidation = {};
    const preferredNetwork = OrchCustomData.srcSdks.orchConfig.preferredNetwork;
    OrchCustomData.apiTimeStamp.initiateIdentityValidation[preferredNetwork] = {};
    OrchCustomData.apiTimeStamp.initiateIdentityValidation[preferredNetwork].startTime = Date.now();
    const initiateIdentityValidationCache =
      OrchCustomData.srcSdks.apiResponseCache.initiateIdentityValidation;
    const loadedSdk = OrchCustomData.srcSdks.loadedSdk;
    const eventId = EVENTS.ORCH_INITIATE_IDENTITY_VALIDATION;
    const instance = loadedSdk[preferredNetwork];
    const timeOutError = {
      error: apiErrors.TIME_OUT
    };
    const promiseRejectError = {
      error: apiErrors.REJECT
    };

    if (OrchCustomData.isVsb) {
      vsbGtmData = { isError: false, isRequest: true, isVsb: OrchCustomData.isVsb };
      trackInitiateIdentityValidation(
        { otp_provider: preferredNetwork },
        OrchCustomData,
        vsbGtmData,
        preferredNetwork
      );
    }

    //MA needs an object(atleast an empty) to pass through the call
    promiseTimeout(
      promiseWrapper(instance, {}, 'initiateIdentityValidation'),
      timeOutError,
      promiseRejectError
    ).then(data => {
      const enrichedData = enrichDataLayer(inputData);
      let gtmData = getGtmData(enrichedData || {});
      initiateIdentityValidationCache[preferredNetwork] = data;
      gtmData = Object.assign(gtmData, VSRCI_OTP_EVENTS);
      gtmData.otp_provider = preferredNetwork;
      gtmData.otp_delivery_channel = getOtpDeliveryChannel(data.maskedValidationChannel);
      trackINOEvent(OTP_EVENT_NAME, gtmData);
      vsbGtmData = {
        isError: Boolean(data?.error),
        isRequest: false,
        isVsb: OrchCustomData.isVsb
      };
      trackInitiateIdentityValidation(gtmData, OrchCustomData, vsbGtmData, preferredNetwork);

      sendMessageToVisaSrci(eventId, data);
      resolve(data);
    });
  });
};

export default initiateIdentityValidation;
